import { createSlice } from "@reduxjs/toolkit";
import { resetAllState } from "./resetAllState";
import { AsyncStatusState, createAsyncStatusState, registerAsync } from "./shared/registerAsync";
import {
  createOneOffSlot,
  createSlotBlock,
  createSlotCalendar,
  createSlotScheduleVersion,
  fetchBookingEntries,
  fetchOwnerSlotAvailability,
  fetchOwnerSlotDetail,
  fetchOwnerSlotSummary,
  fetchPublicSlotAvailability,
  fetchPublicSlotAvailabilitySummary,
  fetchSlotCalendarMeta,
  previewSlotBlock,
  previewSlotCalendarSetup,
  previewSlotSchedule,
  publishSlotCalendar,
  recordBookingEntry,
  SLOT_CALENDAR_NOT_FOUND,
  SLOT_CALENDAR_NOT_OWNER,
  SLOT_CALENDAR_UNSUPPORTED,
  unpublishSlotCalendar,
  updateAvailabilitySlot,
  updateBookingEntry,
} from "../../services/thunks/SlotCalendarThunks";
import {
  SlotAvailabilityPage,
  SlotBlockPreview,
  SlotBookingEntry,
  SlotCalendarMeta,
  SlotCalendarPage,
  SlotCalendarPreview,
  SlotCalendarSummary,
  SlotDisplayKind,
  SlotOwnerAvailabilityPage,
  SlotOwnerAvailabilitySlot,
} from "../../interfaces/SlotCalendarObject";
import { buildAvailabilityRangeKey, buildSummaryRangeKey } from "../../services/helpers/SlotCalendarRangeKeyHelper";

/**
 * Store for the Slot Availability Calendar widget.
 *
 * ONE cache keyed by productId (`byProductId`), the same relationship
 * ProductDetailsDb has to its own `data: Record<string, ProductDetailsModal>`
 * — not admin's "several independent tabs" shape, since there's only one
 * concept here (a listing's calendar), just many listings' worth of it live
 * in the store at once as the person navigates between products.
 *
 * loadingMap/errorMap/successMap stay FLAT at the top level rather than
 * nested per product, exactly like every admin tab does — every key already
 * embeds the productId (see the key builders below), so per-row isolation
 * comes from the key, not from nesting. This is what lets registerAsync's
 * `tab` selector just be `(state) => state` here: the "tab" IS the whole
 * slice, there being only one of it.
 *
 * View-only state (visible month, selected date, which sheet is open) does
 * NOT live here — it's per-mount UI, not app data, and stays in
 * useSlotCalendarView (P2) the way ApprovalsTab's local `pendingDecision`
 * state stays out of the admin slice.
 */

/** notOwner: 14303 (collaborators can see the listing but only the business
 *  owner can manage its calendar). unsupported: 14306/14307. */
export type SlotCalendarAccess = "unknown" | "ownerNotConfigured" | "available" | "notOwner" | "unsupported";

interface SlotSummaryCacheEntry {
  data: SlotCalendarSummary;
  fetchedAt: number;
}

interface SlotPublicAvailabilityCacheEntry {
  data: SlotAvailabilityPage;
  fetchedAt: number;
}

interface SlotOwnerAvailabilityCacheEntry {
  data: SlotOwnerAvailabilityPage;
  fetchedAt: number;
}

export interface SlotCalendarEntry {
  meta: SlotCalendarMeta | null;
  /** Current "cal-{calendarId}-r{revision}" — the If-Match token the next
   *  owner write must send. Only ever set from an owner response; a public
   *  read never receives this header (see SlotCalendarMutationHelper). */
  etag: string | null;
  access: SlotCalendarAccess;
  /** Derived from meta.currentScheduleVersion once an owner read has one.
   *  Still null for a customer-only view until P2 solves the plan doc's
   *  open question (§7 Q2) about exposing `kind` on public reads. */
  displayKind: SlotDisplayKind | null;
  publicAvailability: Record<string, SlotPublicAvailabilityCacheEntry>;
  publicSummaries: Record<string, SlotSummaryCacheEntry>;
  ownerAvailability: Record<string, SlotOwnerAvailabilityCacheEntry>;
  ownerSummaries: Record<string, SlotSummaryCacheEntry>;
  /** Setup's (P5) Step 3/4 dry-run count box — the productless preview, so
   *  it's transient per-wizard-input rather than range-cached like the reads
   *  above. Overwritten on every input change, never merged. */
  setupPreview: SlotCalendarPreview | null;
  /** Manage screen's (P6) Slot sheet - one slot's owner-only detail, keyed by
   *  slotId. */
  ownerSlotById: Record<string, SlotOwnerAvailabilitySlot>;
  /** Manage screen's (P6) Slot sheet bookings list, keyed by slotId - first
   *  page only until P7 adds paging/writes. */
  entriesBySlotId: Record<string, SlotCalendarPage<SlotBookingEntry>>;
  /** Block dates sheet's (P8) dry-run impact - transient like setupPreview,
   *  overwritten on every input change. */
  blockPreview: SlotBlockPreview | null;
  /** Add slots sheet's (P9) next-version dry run - transient, same as above. */
  schedulePreview: SlotCalendarPreview | null;
}

const createEntry = (): SlotCalendarEntry => ({
  meta: null,
  etag: null,
  access: "unknown",
  displayKind: null,
  publicAvailability: {},
  publicSummaries: {},
  ownerAvailability: {},
  ownerSummaries: {},
  setupPreview: null,
  ownerSlotById: {},
  entriesBySlotId: {},
  blockPreview: null,
  schedulePreview: null,
});

export interface SlotCalendarState extends AsyncStatusState {
  byProductId: Record<string, SlotCalendarEntry>;
}

const initialState = (): SlotCalendarState => ({
  ...createAsyncStatusState(),
  byProductId: {},
});

function entryFor(state: SlotCalendarState, productId: string): SlotCalendarEntry {
  if (!state.byProductId[productId]) {
    state.byProductId[productId] = createEntry();
  }
  return state.byProductId[productId];
}

// ─── Async status keys — every key embeds the productId (+ range where the
//     thunk is range-scoped) so acting on one product/month never disables
//     another. Exported for selectors and for the future write-phase thunks
//     to reuse rather than re-deriving their own spelling. ────────────────

export const slotCalendarMetaKey = (productId: string): string => `meta_${productId}`;
export const slotCalendarPublicAvailabilityKey = (productId: string, rangeKey: string): string =>
  `publicAvailability_${productId}_${rangeKey}`;
export const slotCalendarPublicSummaryKey = (productId: string, rangeKey: string): string =>
  `publicSummary_${productId}_${rangeKey}`;
export const slotCalendarOwnerAvailabilityKey = (productId: string, rangeKey: string): string =>
  `ownerAvailability_${productId}_${rangeKey}`;
export const slotCalendarOwnerSummaryKey = (productId: string, rangeKey: string): string =>
  `ownerSummary_${productId}_${rangeKey}`;
/** P4's one write so far - publish. Owner-only, one in flight per product. */
export const slotCalendarPublishKey = (productId: string): string => `publish_${productId}`;
/** P5 Setup's dry-run count box. */
export const slotCalendarSetupPreviewKey = (productId: string): string => `setupPreview_${productId}`;
/** P5 Setup's two chained writes — create the calendar, then its first
 *  schedule version. Separate keys so a resumed draft that skips the first
 *  call never shows a stale "Creating calendar..." spinner. */
export const slotCalendarCreateKey = (productId: string): string => `create_${productId}`;
export const slotCalendarCreateScheduleKey = (productId: string): string => `createSchedule_${productId}`;
/** P6 Manage top bar's Unpublish - separate from publishKey so the two
 *  buttons never share a spinner. */
export const slotCalendarUnpublishKey = (productId: string): string => `unpublish_${productId}`;
/** P6 Slot sheet reads. */
export const slotCalendarOwnerSlotKey = (productId: string, slotId: string): string => `ownerSlot_${productId}_${slotId}`;
export const slotCalendarBookingEntriesKey = (productId: string, slotId: string): string =>
  `bookingEntries_${productId}_${slotId}`;
/** P7 Slot sheet writes. */
export const slotCalendarRecordBookingKey = (productId: string, slotId: string): string =>
  `recordBooking_${productId}_${slotId}`;
export const slotCalendarUpdateEntryKey = (productId: string, entryId: string): string =>
  `updateEntry_${productId}_${entryId}`;
export const slotCalendarUpdateSlotStatusKey = (productId: string, slotId: string): string =>
  `updateSlotStatus_${productId}_${slotId}`;
export const slotCalendarBlockPreviewKey = (productId: string): string => `blockPreview_${productId}`;
export const slotCalendarCreateBlockKey = (productId: string): string => `createBlock_${productId}`;
export const slotCalendarSchedulePreviewKey = (productId: string): string => `schedulePreview_${productId}`;
export const slotCalendarCreateOneOffKey = (productId: string): string => `createOneOff_${productId}`;

// registerAsync's `key` needs the SAME string the thunk's own fulfilled
// payload used for `rangeKey`, so the in-flight loadingMap key (built from
// the arg, before the call resolves) matches the eventual cache key (built
// from the payload, after it resolves) — otherwise a still-loading range and
// its own freshly-cached result would live under two different keys.
function buildKeyForAvailabilityArg(arg: { fromIso: string; toIsoExclusive: string }): string {
  return buildAvailabilityRangeKey(arg);
}
function buildKeyForSummaryArg(arg: { bucket: SlotCalendarSummary["bucket"]; fromDate: string; toDateExclusive: string }): string {
  return buildSummaryRangeKey(arg);
}

const slotCalendarSlice = createSlice({
  name: "SlotCalendarDb",
  initialState: initialState(),
  reducers: {},
  extraReducers: (builder) => {
    registerAsync(builder, fetchSlotCalendarMeta, {
      tab: (state) => state,
      key: (productId) => slotCalendarMetaKey(productId),
      loading: "Loading availability...",
      onFulfilled: (state, payload) => {
        const entry = entryFor(state, payload.productId);
        entry.meta = payload.meta;
        entry.etag = payload.etag;
        entry.access = "available";
        // Resolving SlotDisplayKind needs SlotCalendarDisplayHelper, which is
        // a plain function of (kind, durationUnit) — deferred to the P2
        // component/hook layer rather than computed in the reducer, so the
        // slice doesn't have to import display logic to fill a field nothing
        // reads yet. currentScheduleVersion is already cached on `meta` for
        // whenever that hook wants it.
      },
      onRejected: (state, message, productId) => {
        if (message === SLOT_CALENDAR_NOT_FOUND) {
          entryFor(state, productId).access = "ownerNotConfigured";
          return true; // handled as a state, not an error
        }
        if (message === SLOT_CALENDAR_NOT_OWNER) {
          entryFor(state, productId).access = "notOwner";
          return true;
        }
        if (message === SLOT_CALENDAR_UNSUPPORTED) {
          entryFor(state, productId).access = "unsupported";
          return true;
        }
        return false;
      },
    });

    registerAsync(builder, fetchPublicSlotAvailability, {
      tab: (state) => state,
      key: (arg) => slotCalendarPublicAvailabilityKey(arg.productId, buildKeyForAvailabilityArg(arg)),
      loading: "Loading availability...",
      onFulfilled: (state, payload) => {
        entryFor(state, payload.productId).publicAvailability[payload.rangeKey] = {
          data: payload.page,
          fetchedAt: Date.now(),
        };
      },
    });

    registerAsync(builder, fetchPublicSlotAvailabilitySummary, {
      tab: (state) => state,
      key: (arg) => slotCalendarPublicSummaryKey(arg.productId, buildKeyForSummaryArg(arg)),
      loading: "Loading availability...",
      onFulfilled: (state, payload) => {
        entryFor(state, payload.productId).publicSummaries[payload.rangeKey] = {
          data: payload.summary,
          fetchedAt: Date.now(),
        };
      },
    });

    registerAsync(builder, fetchOwnerSlotAvailability, {
      tab: (state) => state,
      key: (arg) => slotCalendarOwnerAvailabilityKey(arg.productId, buildKeyForAvailabilityArg(arg)),
      loading: "Loading availability...",
      onFulfilled: (state, payload) => {
        entryFor(state, payload.productId).ownerAvailability[payload.rangeKey] = {
          data: payload.page,
          fetchedAt: Date.now(),
        };
      },
    });

    registerAsync(builder, fetchOwnerSlotSummary, {
      tab: (state) => state,
      key: (arg) => slotCalendarOwnerSummaryKey(arg.productId, buildKeyForSummaryArg(arg)),
      loading: "Loading availability...",
      onFulfilled: (state, payload) => {
        entryFor(state, payload.productId).ownerSummaries[payload.rangeKey] = {
          data: payload.summary,
          fetchedAt: Date.now(),
        };
      },
    });

    registerAsync(builder, publishSlotCalendar, {
      tab: (state) => state,
      key: (arg) => slotCalendarPublishKey(arg.productId),
      loading: "Publishing...",
      onFulfilled: (state, payload) => {
        const entry = state.byProductId[payload.productId];
        if (entry) entry.etag = payload.etag;
        // published/revision on `meta` are refreshed by the caller re-
        // dispatching fetchSlotCalendarMeta after a successful publish
        // (useSlotCalendarOwnerState, P4) rather than guessed at here - the
        // mutation result doesn't echo a full SlotCalendarMeta to copy from.
      },
    });

    registerAsync(builder, previewSlotCalendarSetup, {
      tab: (state) => state,
      key: (arg) => slotCalendarSetupPreviewKey(arg.productId),
      loading: "Checking...",
      onFulfilled: (state, payload) => {
        entryFor(state, payload.productId).setupPreview = payload.preview;
      },
    });

    registerAsync(builder, createSlotCalendar, {
      tab: (state) => state,
      key: (arg) => slotCalendarCreateKey(arg.productId),
      loading: "Creating calendar...",
      onFulfilled: (state, payload) => {
        const entry = entryFor(state, payload.productId);
        entry.etag = payload.etag;
        // An already-existing calendar came back with its full meta - cache
        // it so the wizard switches to "resume" mode. A fresh create has no
        // meta to copy (useSlotCalendarSetup refetches after the submit).
        if (payload.existing) {
          entry.meta = payload.existing;
          entry.access = "available";
        }
      },
    });

    registerAsync(builder, createSlotScheduleVersion, {
      tab: (state) => state,
      key: (arg) => slotCalendarCreateScheduleKey(arg.productId),
      loading: "Saving schedule...",
      onFulfilled: (state, payload) => {
        entryFor(state, payload.productId).etag = payload.etag;
      },
    });

    registerAsync(builder, unpublishSlotCalendar, {
      tab: (state) => state,
      key: (arg) => slotCalendarUnpublishKey(arg.productId),
      loading: "Unpublishing...",
      onFulfilled: (state, payload) => {
        const entry = state.byProductId[payload.productId];
        if (entry) entry.etag = payload.etag;
        // Same reasoning as publish's onFulfilled - meta is refreshed by the
        // caller re-dispatching fetchSlotCalendarMeta.
      },
    });

    registerAsync(builder, fetchOwnerSlotDetail, {
      tab: (state) => state,
      key: (arg) => slotCalendarOwnerSlotKey(arg.productId, arg.slotId),
      loading: "Loading slot...",
      onFulfilled: (state, payload) => {
        entryFor(state, payload.productId).ownerSlotById[payload.slot.slotId] = payload.slot;
      },
    });

    registerAsync(builder, fetchBookingEntries, {
      tab: (state) => state,
      key: (arg) => slotCalendarBookingEntriesKey(arg.productId, arg.slotId),
      loading: "Loading bookings...",
      onFulfilled: (state, payload) => {
        entryFor(state, payload.productId).entriesBySlotId[payload.slotId] = payload.page;
      },
    });

    // P7 writes - all three just bump `etag` here. The affected slot detail,
    // its bookings page, the selected day's owner availability, and the
    // month's owner summary are all re-fetched by the caller
    // (useSlotDetailSheet / useSlotCalendarManage) on a successful write,
    // the same "refresh by re-dispatch" pattern every other mutation in this
    // slice already follows, rather than hand-merging a partial result into
    // several caches at once.
    registerAsync(builder, recordBookingEntry, {
      tab: (state) => state,
      key: (arg) => slotCalendarRecordBookingKey(arg.productId, arg.slotId),
      loading: "Saving booking...",
      onFulfilled: (state, payload) => {
        const entry = state.byProductId[payload.productId];
        if (entry) entry.etag = payload.etag;
      },
    });

    registerAsync(builder, updateBookingEntry, {
      tab: (state) => state,
      key: (arg) => slotCalendarUpdateEntryKey(arg.productId, arg.entryId),
      loading: "Updating booking...",
      onFulfilled: (state, payload) => {
        const entry = state.byProductId[payload.productId];
        if (entry) entry.etag = payload.etag;
      },
    });

    registerAsync(builder, updateAvailabilitySlot, {
      tab: (state) => state,
      key: (arg) => slotCalendarUpdateSlotStatusKey(arg.productId, arg.slotId),
      loading: "Updating status...",
      onFulfilled: (state, payload) => {
        const entry = state.byProductId[payload.productId];
        if (entry) entry.etag = payload.etag;
      },
    });

    registerAsync(builder, previewSlotBlock, {
      tab: (state) => state,
      key: (arg) => slotCalendarBlockPreviewKey(arg.productId),
      loading: "Checking...",
      onFulfilled: (state, payload) => {
        entryFor(state, payload.productId).blockPreview = payload.preview;
      },
    });

    registerAsync(builder, createSlotBlock, {
      tab: (state) => state,
      key: (arg) => slotCalendarCreateBlockKey(arg.productId),
      loading: "Blocking dates...",
      onFulfilled: (state, payload) => {
        const entry = state.byProductId[payload.productId];
        if (entry) {
          entry.etag = payload.etag;
          entry.blockPreview = null;
        }
      },
    });

    registerAsync(builder, previewSlotSchedule, {
      tab: (state) => state,
      key: (arg) => slotCalendarSchedulePreviewKey(arg.productId),
      loading: "Checking...",
      onFulfilled: (state, payload) => {
        entryFor(state, payload.productId).schedulePreview = payload.preview;
      },
    });

    registerAsync(builder, createOneOffSlot, {
      tab: (state) => state,
      key: (arg) => slotCalendarCreateOneOffKey(arg.productId),
      loading: "Adding slot...",
      onFulfilled: (state, payload) => {
        const entry = state.byProductId[payload.productId];
        if (entry) entry.etag = payload.etag;
      },
    });

    builder.addCase(resetAllState, () => initialState());
  },
});

// ─── Selectors ────────────────────────────────────────────────────────────

export const selectSlotCalendarEntry = (state: any, productId: string): SlotCalendarEntry | null =>
  state.slotCalendar.byProductId[productId] ?? null;

export const selectSlotCalendarAccess = (state: any, productId: string): SlotCalendarAccess =>
  state.slotCalendar.byProductId[productId]?.access ?? "unknown";

export const selectIsSlotCalendarLoading = (state: any, key: string): boolean =>
  !!state.slotCalendar.loadingMap[key];

export const selectSlotCalendarError = (state: any, key: string): string | null =>
  state.slotCalendar.errorMap[key] ?? null;

export const selectPublicAvailability = (
  state: any,
  productId: string,
  rangeKey: string
): SlotAvailabilityPage | null => state.slotCalendar.byProductId[productId]?.publicAvailability[rangeKey]?.data ?? null;

export const selectPublicSummary = (
  state: any,
  productId: string,
  rangeKey: string
): SlotCalendarSummary | null => state.slotCalendar.byProductId[productId]?.publicSummaries[rangeKey]?.data ?? null;

export const selectOwnerAvailability = (
  state: any,
  productId: string,
  rangeKey: string
): SlotOwnerAvailabilityPage | null =>
  state.slotCalendar.byProductId[productId]?.ownerAvailability[rangeKey]?.data ?? null;

export const selectOwnerSummary = (
  state: any,
  productId: string,
  rangeKey: string
): SlotCalendarSummary | null => state.slotCalendar.byProductId[productId]?.ownerSummaries[rangeKey]?.data ?? null;

export const selectSetupPreview = (state: any, productId: string): SlotCalendarPreview | null =>
  state.slotCalendar.byProductId[productId]?.setupPreview ?? null;

export const selectBlockPreview = (state: any, productId: string): SlotBlockPreview | null =>
  state.slotCalendar.byProductId[productId]?.blockPreview ?? null;

export const selectSchedulePreview = (state: any, productId: string): SlotCalendarPreview | null =>
  state.slotCalendar.byProductId[productId]?.schedulePreview ?? null;

export const selectOwnerSlot = (state: any, productId: string, slotId: string): SlotOwnerAvailabilitySlot | null =>
  state.slotCalendar.byProductId[productId]?.ownerSlotById[slotId] ?? null;

export const selectBookingEntriesPage = (
  state: any,
  productId: string,
  slotId: string
): SlotCalendarPage<SlotBookingEntry> | null => state.slotCalendar.byProductId[productId]?.entriesBySlotId[slotId] ?? null;

export default slotCalendarSlice.reducer;
